import { useState, useMemo, useCallback } from 'react'
import { SlotPropGetter } from 'react-big-calendar'
import addMinutes from 'date-fns/addMinutes'
import areIntervalsOverlapping from 'date-fns/areIntervalsOverlapping'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import getMinutes from 'date-fns/getMinutes'
import isWithinInterval from 'date-fns/isWithinInterval'
import subMinutes from 'date-fns/subMinutes'
import subSeconds from 'date-fns/subSeconds'
import { SortOrder, SortAs } from '@ocean/types/dist/sort'
import { LoggerUtils, SortUtils, DateUtils } from '@ocean/utils'
import { GapInterval, BaseEvent as Event, OnEventDropProps, OnDragStartProps } from '../types'
import BigCalendarUtils from '../utils/BigCalendarUtils'

const useCalendarGaps = (
  events: Event[] | undefined,
  shutdownDurationGap: number,
  launchDurationGap: number
) => {
  const [hiddenGapsEvent, setHiddenGapsEvent] = useState<GapInterval>()
  const MACHINE_TOTAL_REBOOT_DURATION = shutdownDurationGap + launchDurationGap

  const sortedEvents = useMemo(() => {
    if (!events) return []
    return [...events].sort(
      SortUtils.sortByMultipleKeys(['resourceId', 'start'], SortOrder.Ascending, SortAs.Date)
    )
  }, [events])

  const eventShowedGaps = useMemo(() => {
    const showedGaps: Record<string, GapInterval[]> = {}

    try {
      if (sortedEvents.length) {
        for (let i = 0; i < sortedEvents.length; i++) {
          // Add all gaps
          const event = { ...sortedEvents[i] }

          // Check for hidden gaps
          if (
            hiddenGapsEvent &&
            (event.start as Date).getTime() === hiddenGapsEvent.start.getTime() &&
            (event.end as Date).getTime() === hiddenGapsEvent.end.getTime()
          ) {
            continue
          }

          event.start = subMinutes(event.start as Date, launchDurationGap)
          !showedGaps[event.resourceId] && (showedGaps[event.resourceId] = [])

          // Add "before" gap
          showedGaps[event.resourceId].push({
            start: event.start,
            end: subSeconds(sortedEvents[i].start as Date, 1),
          })

          // Add "after" gap
          event.end = addMinutes(sortedEvents[i].end as Date, shutdownDurationGap)
          showedGaps[event.resourceId].push({
            start: sortedEvents[i].end as Date,
            end: subSeconds(event.end, 1),
          })
        }
      }
    } catch (e) {
      LoggerUtils.error('eventShowedGaps error', e)
    }

    return showedGaps
  }, [sortedEvents, hiddenGapsEvent, shutdownDurationGap, launchDurationGap])

  function addGapsToEvent(args: GapInterval) {
    return {
      ...args,
      start: subMinutes(args.start as Date, launchDurationGap),
      end: addMinutes(args.end as Date, shutdownDurationGap),
    }
  }

  function getOverlappedEvents(args: Pick<OnEventDropProps<Event>, 'event' | 'start' | 'end'>) {
    const { id, resourceId } = args.event
    const overlappedEvents: Event[] = []
    // We need to add gaps for correct handling overlapping
    const argsWithGaps = addGapsToEvent(args as GapInterval)

    try {
      // Find all overlapped events
      sortedEvents.forEach(event => {
        const existingEventWithGaps = addGapsToEvent(event as GapInterval)
        if (
          id !== event.id &&
          resourceId === event.resourceId &&
          areIntervalsOverlapping(argsWithGaps as Interval, existingEventWithGaps as Interval)
        ) {
          overlappedEvents.push(event)
        }
      })
    } catch (e) {
      LoggerUtils.error('getOverlappedEvents error', e)
    }

    return overlappedEvents
  }

  function getNextEvent(event: Pick<Event, 'id' | 'resourceId' | 'end'>) {
    // Find the next event after the current event
    const nextEvent = sortedEvents.find(
      ({ start, resourceId, id }) =>
        resourceId === `${event.resourceId}` && id !== event.id && (start as Date) > event.end!
    )

    return nextEvent
  }

  function getNextAccessibleArea(date: Date, currentEvent: Event): GapInterval | undefined {
    try {
      const { resourceId, id, start, end } = currentEvent
      // Get duration of the current event
      const currentEventDuration = differenceInMinutes(end as Date, start as Date)
      // Adjust the end of the current event to keep the duration of the event
      const newCurrentEventEnd = addMinutes(
        date,
        currentEventDuration + MACHINE_TOTAL_REBOOT_DURATION
      )
      const nextEvent = getNextEvent({ id, resourceId, end: date })

      if (
        nextEvent &&
        (nextEvent.start as Date) < addMinutes(newCurrentEventEnd, MACHINE_TOTAL_REBOOT_DURATION)
      ) {
        // If the next event overlapping current event we should find the next accessible area
        return getNextAccessibleArea(nextEvent.end as Date, currentEvent)
      }

      // The start of the current event to the end + reboot time of the last ovelapped event
      const currentEventStart = addMinutes(date, MACHINE_TOTAL_REBOOT_DURATION)
      return {
        start: currentEventStart,
        end: newCurrentEventEnd,
      }
    } catch (e) {
      LoggerUtils.error('getNextAccessibleArea error', e)
    }
  }

  function resetHiddenGapsEvent() {
    setHiddenGapsEvent(undefined)
  }

  function isDateWithinShowedGap(date: Date, gaps: GapInterval[]): boolean {
    if (!gaps) return false

    return gaps.some(gap => {
      let isWithinShowedGap = false
      const { start, end } = gap

      const startDateMinutes = getMinutes(start as Date) % BigCalendarUtils.CALENDAR_STEP
      const normalizedStartDate = subMinutes(start as Date, startDateMinutes)

      try {
        isWithinShowedGap = isWithinInterval(date, { start: normalizedStartDate, end })
      } catch (e) {
        LoggerUtils.error('isWithinInterval error', e)
      }

      return isWithinShowedGap
    })
  }

  const slotPropGetter: SlotPropGetter = (date: Date, resourceId?: number | string) => {
    let result: React.HTMLAttributes<HTMLDivElement> = {}
    if (isDateWithinShowedGap(date, eventShowedGaps[`${resourceId}`])) {
      result.className = 'gapSlot'
    }
    return result
  }

  const handleDragStart = useCallback((args: OnDragStartProps<Event>) => {
    const { start, end } = args.event
    setHiddenGapsEvent({
      start: start as Date,
      end: DateUtils.adjustMinDuration(
        start as Date,
        end as Date,
        BigCalendarUtils.MINIMUM_EVENT_DURATION
      ),
    })
  }, [])

  const handleDragOver = useCallback(() => {
    resetHiddenGapsEvent()
  }, [])

  return {
    getOverlappedEvents,
    resetHiddenGapsEvent,
    getNextAccessibleArea,
    slotPropGetter,
    handleDragStart,
    handleDragOver,
  }
}

export default useCalendarGaps
